/**
 * Level (de)serialization for the editor (Brief 004). Save = stamp identity + emit
 * the canonical LevelFormat JSON as a download; load = parse + structurally validate
 * before it ever reaches the store. A fresh-level factory seeds a playable blank.
 *
 * The editor is the WRITER of LevelFormat (engine reads it, server will store it),
 * so it stamps `meta.levelId = computeLevelId()` (002) + a save timestamp here.
 */
import {
  computeLevelId, EntityType, type LevelFormat, TileType, validateLevel,
} from '../engine/LevelFormat';

/** Stamp identity (createdAt + content levelId) onto a CLONE ready for serialization. */
export function stampForSave(level: LevelFormat): LevelFormat {
  const out: LevelFormat = structuredClone(level);
  out.meta.createdAt = new Date().toISOString();
  out.meta.levelId = computeLevelId(out); // excludes meta → stable under the stamp above
  return out;
}

/** Suggested download/picker filename for a level. */
export function levelFileName(level: LevelFormat): string {
  return `${slug(level.meta.name) || 'level'}.level.json`;
}

/** Serialize → stamp levelId + createdAt → trigger a JSON file download (universal fallback). */
export function downloadLevel(level: LevelFormat): void {
  const out = stampForSave(level);
  const json = JSON.stringify(out, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = levelFileName(out);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── File System Access API save (Brief 004 M1) ───────────────────────────────
// Chromium-only: showSaveFilePicker lets the author pick a real file ONCE and then
// re-save to the SAME file with one key (no re-download spam). Firefox/Safari lack
// it (typeof check below) → callers fall back to downloadLevel + <input type=file>.
// The handle is opaque + not structured-cloneable, so the host holds it in memory.

interface FsWritable { write(data: string): Promise<void>; close(): Promise<void>; }
interface FsFileHandle { createWritable(): Promise<FsWritable>; name?: string; }
interface SaveablePickerWindow {
  showSaveFilePicker(opts: {
    suggestedName?: string;
    types?: { description: string; accept: Record<string, string[]> }[];
  }): Promise<FsFileHandle>;
}

/** True when the browser supports the File System Access save path (Chromium). */
export function fsAccessSupported(): boolean {
  return typeof (window as unknown as Partial<SaveablePickerWindow>).showSaveFilePicker === 'function';
}

/**
 * Save via the File System Access API. Pass the remembered handle to overwrite the
 * same file silently; pass `null`/omit to prompt the picker. Returns the handle so
 * the host can remember it for one-key re-saves. Throws on user-cancel (caller
 * should treat an AbortError as a benign no-op).
 */
export async function saveViaPicker(
  level: LevelFormat,
  existing?: FsFileHandle | null,
): Promise<FsFileHandle> {
  const out = stampForSave(level);
  const json = JSON.stringify(out, null, 2);
  let handle = existing ?? null;
  if (!handle) {
    handle = await (window as unknown as SaveablePickerWindow).showSaveFilePicker({
      suggestedName: levelFileName(out),
      types: [{ description: 'maker level', accept: { 'application/json': ['.json'] } }],
    });
  }
  const writable = await handle.createWritable();
  await writable.write(json);
  await writable.close();
  return handle;
}

/** Opaque handle type re-exported for the host to store. */
export type LevelFileHandle = FsFileHandle;

/** Parse + validate JSON text into a LevelFormat. Returns errors instead of throwing. */
export function parseLevel(text: string): { level?: LevelFormat; errors: string[] } {
  let data: unknown;
  try { data = JSON.parse(text); } catch { return { errors: ['not valid JSON'] }; }
  const shape = shapeErrors(data);
  if (shape.length) return { errors: shape };
  const level = data as LevelFormat;
  const errs = validateLevel(level);
  if (errs.length) return { errors: errs };
  return { level, errors: [] };
}

/** Minimal structural guard before trusting the object as a LevelFormat. */
function shapeErrors(data: unknown): string[] {
  if (typeof data !== 'object' || data === null) return ['root is not an object'];
  const d = data as Record<string, unknown>;
  const errs: string[] = [];
  if (typeof d.version !== 'number') errs.push('missing version');
  if (typeof d.dimensions !== 'object' || d.dimensions === null) errs.push('missing dimensions');
  if (!Array.isArray(d.tiles)) errs.push('tiles is not an array');
  if (!Array.isArray(d.entities)) errs.push('entities is not an array');
  if (typeof d.theme !== 'string') errs.push('missing theme');
  return errs;
}

/** A fresh, immediately-playable blank level (decision E5: default 80×16). */
export function blankLevel(widthTiles = 80, heightTiles = 16, tileSize = 64): LevelFormat {
  const tiles: TileType[][] = Array.from({ length: heightTiles }, () => Array<TileType>(widthTiles).fill(TileType.EMPTY));
  // floor (bottom 2 rows) + side walls so a new canvas is bounded + walkable
  for (let c = 0; c < widthTiles; c++) { tiles[heightTiles - 1][c] = TileType.SOLID; tiles[heightTiles - 2][c] = TileType.SOLID; }
  for (let r = 0; r < heightTiles; r++) { tiles[r][0] = TileType.SOLID; tiles[r][widthTiles - 1] = TileType.SOLID; }
  return {
    version: 1,
    meta: { name: 'Untitled', author: 'editor', createdAt: new Date().toISOString(), formProfile: 'smb1' },
    dimensions: { widthTiles, heightTiles, tileSize },
    tiles,
    entities: [
      { type: EntityType.PLAYER_START, x: 2, y: heightTiles - 3 },
      { type: EntityType.GOAL_FLAG, x: widthTiles - 4, y: heightTiles - 3 },
    ],
    theme: 'overworld',
  };
}

function slug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
